// 🔬 B242 (AI_110) — MOTOR DE COORTE (determinístico, puro, testável)
// Aplica filtros sobre RegistroPesquisa[] e monta a Table 1 descritiva.
// REGRA DE OURO: aqui é só CÓDIGO contando/calculando — sem IA.
import {
  RegistroPesquisa,
  Filtro,
  GrupoFiltros,
  CampoPesquisa,
  LinhaTabela1,
  CelulaTabela1,
} from "../types";
import {
  resumirNumerico,
  resumirCategorico,
  paraNumero,
  ehNumeroPequeno,
} from "./estatisticasDescritivas";

// Normaliza texto p/ comparação (minúsculas, sem acento, trim).
function norm(v: unknown): string {
  return String(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // remove marcas de acento combinantes
    .trim()
    .toLowerCase();
}

function ehFaltante(v: unknown): boolean {
  return v === null || v === undefined || v === "";
}

function arred(x: number, casas = 1): number {
  const f = Math.pow(10, casas);
  return Math.round(x * f) / f;
}

// Decide se UM registro satisfaz UM filtro.
export function aplicarFiltro(registro: RegistroPesquisa, filtro: Filtro): boolean {
  const v = registro[filtro.campo];

  switch (filtro.operador) {
    case "presente":
      return !ehFaltante(v);
    case "ausente":
      return ehFaltante(v);
  }

  // Operadores abaixo exigem valor presente no registro.
  if (ehFaltante(v)) return false;

  switch (filtro.operador) {
    case "maior":
    case "maiorIgual":
    case "menor":
    case "menorIgual":
    case "entre": {
      const nv = paraNumero(v);
      const a = paraNumero(filtro.valor);
      if (nv === null || a === null) return false;
      if (filtro.operador === "maior") return nv > a;
      if (filtro.operador === "maiorIgual") return nv >= a;
      if (filtro.operador === "menor") return nv < a;
      if (filtro.operador === "menorIgual") return nv <= a;
      // entre [a, b] inclusivo
      const b = paraNumero(filtro.valor2);
      if (b === null) return false;
      return nv >= Math.min(a, b) && nv <= Math.max(a, b);
    }
    case "igual":
    case "diferente": {
      // Compara numericamente quando ambos são números; senão, texto normalizado.
      const nv = paraNumero(v);
      const na = paraNumero(filtro.valor);
      const iguais =
        nv !== null && na !== null ? nv === na : norm(v) === norm(filtro.valor);
      return filtro.operador === "igual" ? iguais : !iguais;
    }
    case "contem":
      return norm(v).includes(norm(filtro.valor));
    default:
      return false;
  }
}

// Aplica um grupo de filtros (conector E/OU) sobre a base.
// Grupo vazio → retorna todos (coorte = base inteira).
export function aplicarFiltros(
  registros: RegistroPesquisa[],
  grupo: GrupoFiltros
): RegistroPesquisa[] {
  if (!grupo.filtros || grupo.filtros.length === 0) return [...registros];
  return registros.filter((r) =>
    grupo.conector === "OU"
      ? grupo.filtros.some((f) => aplicarFiltro(r, f))
      : grupo.filtros.every((f) => aplicarFiltro(r, f))
  );
}

// ── Table 1 ──────────────────────────────────────────────────────────────
// Para cada campo, gera células-resumo (por coluna/estrato). Sem estratificação
// há apenas a coluna "Total". Faltantes são calculados sobre a coorte inteira.

function celulasDeCampo(
  registros: RegistroPesquisa[],
  campo: CampoPesquisa
): CelulaTabela1[] {
  const valores = registros.map((r) => r[campo.chave]);

  if (campo.tipo === "numerico") {
    const s = resumirNumerico(valores);
    if (s.n === 0) return [{ rotulo: "—", texto: "sem dados" }];
    const dp = s.desvioPadrao !== null ? ` (${arred(s.desvioPadrao)})` : "";
    return [
      {
        rotulo: "Mediana [Q1–Q3]",
        texto: `${arred(s.mediana as number)} [${arred(s.q1 as number)}–${arred(
          s.q3 as number
        )}]`,
        n: s.n,
      },
      {
        rotulo: "Média (DP)",
        texto: `${arred(s.media as number)}${dp}`,
        n: s.n,
      },
      {
        rotulo: "Mín–Máx",
        texto: `${arred(s.min as number)}–${arred(s.max as number)}`,
        n: s.n,
      },
    ];
  }

  // categórico / booleano
  if (campo.tipo === "categorico" || campo.tipo === "booleano") {
    const s = resumirCategorico(valores);
    if (s.n === 0) return [{ rotulo: "—", texto: "sem dados" }];
    return s.categorias.map((c) => ({
      rotulo: c.valor,
      texto: `${c.n} (${arred(c.proporcao * 100)}%)`,
      n: c.n,
      pequeno: c.pequeno,
    }));
  }

  // texto livre — no MVP1 só conta preenchidos (sem distribuição)
  const preenchidos = valores.filter((v) => !ehFaltante(v)).length;
  return [
    {
      rotulo: "preenchidos",
      texto: String(preenchidos),
      n: preenchidos,
      pequeno: ehNumeroPequeno(preenchidos),
    },
  ];
}

export interface OpcoesTabela1 {
  estratificarPor?: string; // chave de um campo categórico/booleano
}

export function montarTabela1(
  registros: RegistroPesquisa[],
  campos: CampoPesquisa[],
  opcoes: OpcoesTabela1 = {}
): LinhaTabela1[] {
  // Define as colunas: "Total" + um estrato por valor distinto (se pedido).
  const colunas: { coluna: string; registros: RegistroPesquisa[] }[] = [
    { coluna: "Total", registros },
  ];

  if (opcoes.estratificarPor) {
    const chave = opcoes.estratificarPor;
    const grupos = new Map<string, RegistroPesquisa[]>();
    for (const r of registros) {
      const v = r[chave];
      if (ehFaltante(v)) continue; // estrato faltante fica fora das colunas
      const k = typeof v === "boolean" ? (v ? "Sim" : "Não") : String(v).trim();
      if (!grupos.has(k)) grupos.set(k, []);
      grupos.get(k)!.push(r);
    }
    Array.from(grupos.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .forEach(([k, regs]) => colunas.push({ coluna: k, registros: regs }));
  }

  const total = registros.length;

  return campos.map((campo) => {
    const valores = registros.map((r) => r[campo.chave]);
    const faltantes = valores.filter(ehFaltante).length;
    return {
      campo: campo.chave,
      label: campo.label,
      tipo: campo.tipo,
      faltantes,
      faltantesPct: total > 0 ? arred((faltantes / total) * 100) : 0,
      celulasPorColuna: colunas.map((c) => ({
        coluna: c.coluna,
        celulas: celulasDeCampo(c.registros, campo),
      })),
    };
  });
}
